<style lang="scss">
.tz-paginate {
  &__list {
    display: flex;
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
  &__item {
    display: inline-block;
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
  &__link,
  &__previous,
  &__next {
    display: block;
    text-decoration: none;
    text-align: center;
  }
  &__link {
    &--current,
    &--current:hover {
      pointer-events: none;
    }
  }
  &__previous,
  &__next {
    background: {
      repeat: no-repeat;
      position: center;
    }
    padding: 0;
    &--disabled {
      opacity: 0.5;
    }
  }
}
</style>

<template>
  <nav class="tz-paginate" aria-label="pagination" v-if="numberOfPages > 1">
    <ul class="tz-paginate__list">
      <!--- Previous Arrow -->
      <li
        v-for="item in renderIf(showArrowButtons)"
        :key="item"
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-arrows"
      >
        <!--  NOTE: You will have a vue warning in your browser console that states that router-link is an unknown element. This is okay as it gets defined in framework, it will however affect this locally run component's search bar url when useRouterLinks prop is used. Inspect the elements "to" prop to ensure the correct url will be used on Framework-->
        <router-link
          v-if="useRouterLinks"
          :to="previousPageHref"
          class="tz-paginate__previous"
          :class="{ 'tz-paginate__previous--disabled': isFirstPage }"
          @click="onClickPrevious()"
          :aria-disabled="isFirstPage"
          role="button"
        ></router-link>
        <a
          v-else
          :href="previousPageHref"
          class="tz-paginate__previous"
          :class="{ 'tz-paginate__previous--disabled': isFirstPage }"
          :aria-label="'Goto Previous Page'"
          @click="onClickPrevious()"
          role="button"
          :aria-disabled="isFirstPage"
        ></a>
      </li>

      <!-- Optional first two pages -->
      <li
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-first"
        v-for="page in showFirstPages ? firstPages : []"
        :key="page"
      >
        <router-link
          v-if="useRouterLinks"
          :to="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          @click="onClickPage(page)"
          :aria-label="getAriaLabel(page)"
          role="button"
          >{{ page }}</router-link
        >
        <a
          v-else
          :href="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          :aria-label="getAriaLabel(page)"
          @click="onClickPage(page)"
          role="button"
          >{{ page }}</a
        >
      </li>
      <!-- Optional divider -->
      <li
        v-for="item in renderIf(showFirstPages)"
        :key="item"
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-first"
      >
        <div class="tz-paginate__link tz-paginate__link--ellipsis">...</div>
      </li>
      <!--- Pages -->
      <li class="tz-paginate__item" v-for="page in displayPages" :key="page">
        <router-link
          v-if="useRouterLinks"
          :to="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          @click="onClickPage(page)"
          :aria-label="getAriaLabel(page)"
          role="button"
          >{{ page }}</router-link
        >
        <a
          v-else
          :href="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          :aria-label="getAriaLabel(page)"
          @click="onClickPage(page)"
          role="button"
          >{{ page }}</a
        >
      </li>
      <!-- Optional divider for last pages -->
      <li
        v-for="item in renderIf(showLastPages)"
        :key="item"
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-last"
      >
        <div class="tz-paginate__link tz-paginate__link--ellipsis">...</div>
      </li>
      <!-- Optional display of last two pages -->
      <li
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-last"
        v-for="page in showLastPages ? lastPages : []"
        :key="page"
      >
        <router-link
          v-if="useRouterLinks"
          :href="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          :aria-label="getAriaLabel(page)"
          @click="onClickPage(page)"
          role="button"
          >{{ page }}</router-link
        >
        <a
          v-else
          :href="getPageHref(page)"
          class="tz-paginate__link"
          :class="{ 'tz-paginate__link--current': isCurrentPage(page) }"
          :aria-label="getAriaLabel(page)"
          @click="onClickPage(page)"
          role="button"
          >{{ page }}</a
        >
      </li>
      <!--- Next Arrow -->
      <li
        v-for="item in renderIf(showArrowButtons)"
        :key="item"
        class="tz-paginate__item tz-paginate__item--navigation tz-paginate__item--navigation-arrows"
      >
        <router-link
          v-if="useRouterLinks"
          :to="nextPageHref"
          class="tz-paginate__next"
          :class="{ 'tz-paginate__next--disabled': isLastPage }"
          :aria-label="'Goto Next Page'"
          @click="onClickNext()"
          role="button"
          :aria-disabled="isLastPage"
        ></router-link>
        <a
          v-else
          :href="nextPageHref"
          class="tz-paginate__next"
          :class="{ 'tz-paginate__next--disabled': isLastPage }"
          :aria-label="'Goto Next Page'"
          @click="onClickNext()"
          role="button"
          :aria-disabled="isLastPage"
        ></a>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'TzPaginate',
  computed: {
    // Return the page numbers to be displayed
    displayPages() {
      let range = [];

      for (let i = this.startingPage; i <= this.endingPage; i += 1) {
        range.push(i);
      }

      return range;
    },
    // Return the last possible page number
    endingPage() {
      return Math.min(
        this.startingPage + this.maxPages - 1,
        this.numberOfPages
      );
    },
    isFirstPage() {
      return this.currentPage === 1;
    },
    isLastPage() {
      return this.currentPage === this.numberOfPages;
    },
    // Last two page numbers
    lastPages() {
      return [this.numberOfPages - 1, this.numberOfPages];
    },
    // Return the grouping of last pages that can be cycled through
    lastPossibleStart() {
      return this.numberOfPages - this.maxPages + 1;
    },
    nextPageHref() {
      if (this.currentPage === this.numberOfPages && this.useRouterLinks) {
        return `${this.baseUrl}/${this.currentPage}/`;
      }
      if (this.currentPage < this.numberOfPages) {
        return this.getPageHref(this.currentPage + 1);
      }

      return null;
    },
    numberOfPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    // Return the page number that will trigger display of first pages
    pageToChangeNumbersDisplayed() {
      if (this.maxPages === 3) {
        return 4;
      }
      if (this.maxPages < 6) {
        return 5;
      }

      return this.maxPages - 1;
    },
    previousPageHref() {
      if (this.currentPage === 1 && this.useRouterLinks) {
        return `${this.baseUrl}/`;
      }
      if (this.currentPage > 1) {
        return this.getPageHref(this.currentPage - 1);
      }

      return null;
    },
    showFirstAndLast() {
      // Only display first and last if more than two pages are displayed,
      // and if we will not be replacing a single sequential number with ...
      return this.maxPages > 2 && this.maxPages + 3 < this.numberOfPages
        ? this.displayFirstAndLast
        : false;
    },
    showFirstPages() {
      if (!this.showFirstAndLast) {
        return false;
      }

      // Show if first numbers not exposed
      return !this.firstPages.some(p => this.displayPages.includes(p));
    },
    showLastPages() {
      if (!this.showFirstAndLast) {
        return false;
      }

      // Show if last number is not exposed
      return !this.lastPages.some(p => this.displayPages.includes(p));
    },
    startingPage() {
      // Within first page block, always return 1
      if (
        this.isFirstPage ||
        this.currentPage < this.pageToChangeNumbersDisplayed
      ) {
        return 1;
      }

      // If this is the last page, or within the last pages, display last grouping of pages
      if (this.isLastPage || this.currentPage >= this.lastPossibleStart) {
        return this.lastPossibleStart;
      }

      // If we are not displaying first and last page numbers, skip the rest of the logic and just cycle through naturally
      if (!this.displayFirstAndLast) {
        // Maintain the minimum number of pages for starting number
        return this.currentPage - 1;
      }

      // Halfway point when numbers are displayed
      const halfway = this.currentPage - Math.floor(this.maxPages / 2);

      // If halfway point should move page forward, and will not expose the final pages
      if (
        halfway > this.pageToChangeNumbersDisplayed &&
        halfway + this.maxPages <= this.numberOfPages
      ) {
        // Use halfway point if it does not display last pages, otherwise step back one number
        return this.lastPages.includes(halfway + this.maxPages)
          ? halfway - 1
          : halfway;
      }

      const lastPageIncludesDisplayedNumbers = this.lastPages.includes(
        this.pageToChangeNumbersDisplayed + this.maxPages
      );

      // Otherwise if that last page includes displayed numbers, take a step back, otherwise use it
      return lastPageIncludesDisplayedNumbers
        ? this.pageToChangeNumbersDisplayed - 1
        : this.pageToChangeNumbersDisplayed;
    }
  },
  data() {
    return {
      firstPages: [1, 2]
    };
  },
  methods: {
    /**
     * If statement vue hack to prevent <!-- --> comment
     * @param condition
     * @returns {[]}
     */
    renderIf(condition) {
      return condition ? [Math.random()] : [];
    },
    getPageHref(page) {
      return page === 1
        ? this.baseUrl + (this.hasTrailingSlash ? '/' : '')
        : this.baseUrl + '/' + page + (this.hasTrailingSlash ? '/' : '');
    },
    getAriaLabel(page) {
      const text = 'Goto Page ' + page;

      if (!this.isCurrentPage(page)) {
        return text;
      }

      return text + ', Current Page';
    },
    isCurrentPage(page) {
      return +page === +this.currentPage;
    },
    onClickNext() {
      // Only emit event if the next page is actual page
      if (this.currentPage + 1 <= this.numberOfPages) {
        this.$emit('page-changed', this.currentPage + 1);
      }
    },
    onClickPage(page) {
      this.$emit('page-changed', page);
    },
    onClickPrevious() {
      // Only emit event if previous page click is to actual page
      if (this.currentPage - 1 > 0) {
        this.$emit('page-changed', this.currentPage - 1);
      }
    }
  },
  props: {
    // URL For Links
    baseUrl: {
      type: String,
      required: true
    },
    // Page we are on
    currentPage: {
      type: Number,
      required: true
    },
    // True if first and last pages should be displayed during scroll through, will only display if max pages is 3+
    displayFirstAndLast: {
      type: Boolean,
      default: false
    },
    // Number of items to display per page
    itemsPerPage: {
      type: Number,
      default: 100
    },
    // Total number of pages to show at a time
    maxPages: {
      type: Number,
      default: 5
    },
    // Total number of items we will display
    totalItems: {
      type: Number,
      required: true
    },
    // Adds trailing slash to paginated links
    hasTrailingSlash: {
      type: Boolean,
      default: false
    },
    // whether to show right and left navigation arrows
    showArrowButtons: {
      type: Boolean,
      default: false
    },
    // whether to use router-link for client navigation instead of anchor tags
    useRouterLinks: {
      type: Boolean,
      default: false
    }
  }
};
</script>
